import {Button, Col, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useForm} from "react-hook-form";
import {t} from "i18next";
import Field from "../Field";
import './style.scss';
import InsuranceCheckbox from "./InsuranceCheckbox";
import FormButton from "../buttons/main";
import BirthdateRHF from "../BirthdateRHF";
import { useEffect } from "react";
import { observer } from 'mobx-react-lite';
import { useInsuranceApi } from 'lib/api/useInsuranceApi';
import dayjs from "dayjs";
import cross from '../../../styles/assets/images/sv/cross_black.svg';
import InsuranceLogo from '../../../styles/assets/images/sv/rosgosstrah_logo.png';
import BookingEventDetails from "../BookingEventDetails";
import {PRODUCT_TYPES} from "sv-common/constants/certificates";
import { Trans } from 'react-i18next';
import useUserData from "../../../lib/utils/hooks/useUserData";
import {sendYandexMetrikaRgsEvent} from '../../../lib/utils/analytics'

const InsuranceModal = ({handleModalVisible, defaultValues, order, productType, onSuccess}) => {
    const userData = useUserData();

    const {control, register, handleSubmit, setValue, formState: { errors, isValid, isDirty }, watch} = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            firstName: defaultValues?.first_name || '',
            middleName: defaultValues?.middleName || '',
            lastName: defaultValues?.last_name || '',
            birthDate: dayjs(defaultValues?.birthdate || '').format('DD.MM.YYYY') || '',
            email: defaultValues?.email || '',
            agreeWithInsuranceTerms: false
        }
    });

    useEffect(() => {
        if (!defaultValues || !userData) return;

        const hasDefaultValues = Object.keys(defaultValues).length > 0;
        const hasUserData = Object.keys(userData).length > 0;

        if (hasDefaultValues && hasUserData) {
            if (userData.birthdate) setValue('birthDate', dayjs(userData?.birthdate || '').format('DD.MM.YYYY'));
            setValue('firstName', userData?.first_name || '');
            setValue('lastName', userData?.last_name || '');
            setValue('email', defaultValues?.email || '');
        }
    }, [defaultValues?.email, userData?.birthdate, userData?.first_name, userData?.last_name])

    const { createInsurance } = useInsuranceApi();

    const onSubmit = (data) => {
        delete data.agreeWithInsuranceTerms;
        data.email = defaultValues?.email || '';
        data.birthDate = dayjs(data.birthDate, "DD.MM.YYYY").format('YYYY-MM-DD');
        sendYandexMetrikaRgsEvent('rgs-click_button-issued_free')

        createInsurance({orderId: defaultValues.orderId, userDetails: {...data}})
            .then(() => {
                sendYandexMetrikaRgsEvent('rgs-success');
                onSuccess();
            })
            .catch(e => {
                console.error(e.response?.data?.message)
            })
            .finally(() => handleModalVisible(false))
    };
    const isConsentChecked = watch('agreeWithInsuranceTerms');
    const isSubmitReady = isValid && isDirty && isConsentChecked;

    const getEventName = (productType) => {
        const eventTypes = {
            [PRODUCT_TYPES.SLOTS]: order?.slot?.name,
            [PRODUCT_TYPES.ARRIVALS]: order?.arrival?.name,
        };
        return eventTypes[productType];
    }

    const eventInfoMap = {
        [PRODUCT_TYPES.SLOTS]: {slot: order?.slot, base: order?.base, eventHideEventPlace: order?.eventHideEventPlace},
        [PRODUCT_TYPES.ARRIVALS]: {arrival: order?.arrival},
    }
    const eventInfo = eventInfoMap[productType];

    return (
        <Modal
            size="lg" style={{maxWidth: '600px', width: '100%'}}
            isOpen
            className={'insurance-modal'}
            toggle={() => handleModalVisible()}
            centered
        >
            <Button className={'close-modal'} onClick={() => handleModalVisible(false)}>
                <img src={cross} alt=""/>
            </Button>
            <ModalHeader>
                <span className="outlined-title">{t('common.insuranceModal.headerLabel')}</span>
            </ModalHeader>
            <ModalBody>
                <img src={InsuranceLogo} alt={t('preparationSteps.insuranceCompany')} className="insurance-modal__logo" />
                <h3 className="insurance-modal__title">{t('common.insuranceModal.title')}</h3>
                <h4 className="insurance-modal__subtitle">{t('common.insuranceModal.howItWorks')}</h4>
                <ol className="insurance-modal__list">
                    <li>
                        <p>{t('common.insuranceModal.steps.1')}</p>
                        <div className="insurance-modal__event">{getEventName(productType)}</div>
                        <BookingEventDetails eventInfo={eventInfo} productType={productType}/>
                    </li>
                    <li>
                        <p>{t('common.insuranceModal.steps.2')}</p>
                    </li>
                    <li>
                        <p>
                            <Trans
                                i18nKey="common.insuranceModal.steps.3"
                                values={{
                                    orderName: getEventName(productType)
                                }}
                                components={{
                                    1: <a href="https://www.silavetra.rgs.online/?campaign=SLVTR&media_source=0000&utm_source=web&utm_medium=landing&utm_campaign=sv_cmp_1_sport" target="_blank" className="insurance-modal__link" />
                                }}
                            />
                        </p>
                    </li>
                </ol>
                <form onSubmit={handleSubmit(onSubmit)} action="">
                    <div className="data-review">
                        <Row className="data-review__title">
                            <Col xs={12} md={12}>{t('common.insuranceModal.dataReview')}</Col>
                        </Row>
                        <Row className="data-review__notice">
                            <Col>{t('common.insuranceModal.dataReviewNotice')}</Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={6} className={'control-wrapper'}>
                                <Field
                                    label={t('inputs.firstName.label')}
                                    error={errors['firstName']}
                                    placeholder={t('inputs.firstName.label')}
                                    fieldName="firstName"
                                    register={register}
                                    required
                                    className="field"
                                />
                            </Col>
                            <Col xs={12} md={6} className={'control-wrapper'}>
                                <Field
                                    label={t('inputs.middleName.label')}
                                    error={errors['middleName']}
                                    placeholder={t('inputs.middleName.label')}
                                    fieldName="middleName"
                                    register={register}
                                    required
                                    className="field"
                                />
                            </Col>
                            <Col xs={12} md={6} className={'control-wrapper'}>
                                <Field
                                    label={t('inputs.lastName.label')}
                                    error={errors['lastName']}
                                    placeholder={t('inputs.lastName.label')}
                                    fieldName="lastName"
                                    register={register}
                                    required
                                    className="field"
                                />
                            </Col>
                            <Col xs={12} md={6} className={'control-wrapper'}>
                                <BirthdateRHF
                                    register={register}
                                    fieldName="birthDate"
                                    control={control}
                                    required
                                    error={errors['birthDate']}
                                />
                            </Col>
                            <Col xs={12} md={12} className={'control-wrapper'}>
                                <Field
                                    label={t('inputs.emailShort.label')}
                                    error={errors['email']}
                                    placeholder={t('inputs.emailShort.placeholder')}
                                    fieldName="email"
                                    register={register}
                                    required
                                    readOnly
                                    className="field"
                                />
                            </Col>
                        </Row>
                        <Row className="data-review__notice data-review__important">
                            <Col>{t('common.insuranceModal.formRequirements')}</Col>
                        </Row>
                    </div>
                    <Row>
                        <Col xs={12} md={12}>
                            <InsuranceCheckbox
                                register={register}
                                name="agreeWithInsuranceTerms"
                                required={t('common.fields.insurance.validation')}
                                text=""
                                error={errors['agreeWithConcent']}
                            >
                                <Trans
                                    i18nKey="common.fields.insurance.label"
                                    components={{
                                        1: <a target="_blank" rel="noopener noreferrer" href="http://www.silavetra.rgs.online" />,
                                        2: <a target="_blank" rel="noopener noreferrer" href="https://silavetra.com/legal/personal-data" />,
                                    }}
                                />
                            </InsuranceCheckbox>
                        </Col>
                    </Row>
                    <FormButton disabled={!isSubmitReady} type="submit" value={t('common.insuranceModal.button')} className={"btn-muted"}/>
                    <Row>
                        <Col className="insurance-modal__notice">{t('common.insuranceModal.selfCoverageOnly')}</Col>
                    </Row>
                </form>
            </ModalBody>
        </Modal>
    )
}

export default observer(InsuranceModal);